// src/pages/MFASetupPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Shield, Copy, Check, AlertCircle } from 'lucide-react';
import './MFASetupPage.css';

const MFASetupPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [, setStep] = useState<'generate' | 'verify'>('generate');
    const [qrCode, setQrCode] = useState<string>('');
    const [secret, setSecret] = useState<string>('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [verificationCode, setVerificationCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState('');
    const [copiedSecret, setCopiedSecret] = useState(false);
    const [copiedCodes, setCopiedCodes] = useState(false);

    const generateMFA = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/mfa/setup', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setQrCode(data.qrCode);
                setSecret(data.secret);
                setBackupCodes(data.backupCodes);
            } else {
                throw new Error('Erro ao gerar MFA');
            }
        } catch (err) {
            console.error('Erro ao gerar MFA:', err);
            alert('Erro ao configurar MFA');
            navigate('/student-area');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        // Verificar se usuário pode ativar MFA
        if (user?.role !== 'admin' && user?.role !== 'vendor') {
            alert('MFA disponível apenas para Admin e Vendor');
            navigate('/student-area');
            return;
        }

        generateMFA();
    }, [user?.role, navigate, generateMFA]);

    const handleVerify = async () => {
        if (verificationCode.length !== 6) {
            setError('Digite o código de 6 dígitos');
            return;
        }

        setVerifying(true);
        setError('');

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/mfa/verify-setup', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ token: verificationCode })
            });

            if (response.ok) {
                setStep('verify');
                alert('✅ MFA ativado com sucesso!');
                navigate('/student-area');
            } else {
                const data = await response.json();
                setError(data.message || 'Código inválido');
            }
        } catch (err) {
            setError('Erro ao verificar código');
        } finally {
            setVerifying(false);
        }
    };

    const copySecret = () => {
        navigator.clipboard.writeText(secret);
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
    };

    const copyBackupCodes = () => {
        navigator.clipboard.writeText(backupCodes.join('\n'));
        setCopiedCodes(true);
        setTimeout(() => setCopiedCodes(false), 2000);
    };

    if (loading) {
        return (
            <div className="mfasp-page">
                <div className="mfasp-loading-container">
                    <div className="mfasp-spinner"></div>
                    <p className="mfasp-loading-text">Carregando...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mfasp-page">
            <header className="mfasp-header">
                <button className="mfasp-btn-back" onClick={() => navigate('/student-area')}>
                    <ArrowLeft size={20} />
                    Voltar
                </button>
                <div className="mfasp-header-logo">
                    <Shield size={24} />
                    <h1 className="mfasp-header-title">Configurar Autenticação de Dois Fatores</h1>
                </div>
            </header>

            <div className="mfasp-content">
                <div className="mfasp-card">
                    <div className="mfasp-step">
                        <span className="mfasp-step-number">1</span>
                        <h3 className="mfasp-step-title">Escaneie o QR Code</h3>
                        <p className="mfasp-step-description">Use um app autenticador como Google Authenticator, Authy ou Microsoft Authenticator</p>

                        <div className="mfasp-qr-container">
                            <img src={qrCode} alt="QR Code" className="mfasp-qr-code" />
                        </div>

                        <div className="mfasp-secret-container">
                            <p className="mfasp-secret-label">Ou digite manualmente:</p>
                            <div className="mfasp-secret-box">
                                <code className="mfasp-secret-code">{secret}</code>
                                <button className="mfasp-btn-copy" onClick={copySecret}>
                                    {copiedSecret ? <Check size={18} /> : <Copy size={18} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="mfasp-step">
                        <span className="mfasp-step-number">2</span>
                        <h3 className="mfasp-step-title">Salve os Códigos de Backup</h3>
                        <p className="mfasp-step-description">Guarde esses códigos em um lugar seguro. Cada código pode ser usado apenas uma vez.</p>

                        <div className="mfasp-backup-codes-container">
                            <div className="mfasp-backup-codes-grid">
                                {backupCodes.map((code, index) => (
                                    <div key={index} className="mfasp-backup-code">{code}</div>
                                ))}
                            </div>
                            <button className="mfasp-btn-copy-codes" onClick={copyBackupCodes}>
                                {copiedCodes ? <Check size={18} /> : <Copy size={18} />}
                                {copiedCodes ? 'Copiado!' : 'Copiar Todos'}
                            </button>
                        </div>

                        <div className="mfasp-warning-box">
                            <AlertCircle size={20} />
                            <p className="mfasp-warning-text">
                                <strong>Importante:</strong> Guarde esses códigos com segurança.
                                Eles são a única forma de acessar sua conta se você perder acesso ao app autenticador.
                            </p>
                        </div>
                    </div>

                    <div className="mfasp-step">
                        <span className="mfasp-step-number">3</span>
                        <h3 className="mfasp-step-title">Verificar Configuração</h3>
                        <p className="mfasp-step-description">Digite o código de 6 dígitos do seu app autenticador</p>

                        <div className="mfasp-verification-container">
                            <input
                                type="text"
                                maxLength={6}
                                placeholder="000000"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                className="mfasp-verification-input"
                            />
                            {error && (
                                <div className="mfasp-error-message">
                                    <AlertCircle size={16} />
                                    {error}
                                </div>
                            )}
                            <button
                                className="mfasp-btn-verify"
                                onClick={handleVerify}
                                disabled={verifying || verificationCode.length !== 6}
                            >
                                {verifying ? 'Verificando...' : 'Ativar MFA'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MFASetupPage;